import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaBell, FaUser, FaSignOutAlt, FaCog, FaChevronDown, FaSearch, FaExpandArrowsAlt, FaCompress, FaBars } from "react-icons/fa";
import { Dropdown } from "react-bootstrap";
import Cookies from 'js-cookie';

// Import the new stylesheet
import '../components/Header.css';

export default function Header({ pageTitle, isMobile, globalSearch, setGlobalSearch }) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user")) || {};
  const roleFromCookie = Cookies.get('userRole');

  const getDisplayName = (userObj) => {
    if (!userObj) return "Guest";
    return userObj.name || userObj.username || `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim() || "User";
  };

  const username = getDisplayName(user);
  const userRole = roleFromCookie ? (roleFromCookie.charAt(0).toUpperCase() + roleFromCookie.slice(1)) : (user.role || "User");

  const [notifications] = useState([
    { id: 1, title: "New student registration", time: "2m ago", type: "info" },
    { id: 2, title: "Semester grades uploaded", time: "15m ago", type: "success" },
    { id: 3, title: "System maintenance scheduled", time: "1h ago", type: "warning" }
  ]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("user");
    Cookies.remove('token');
    Cookies.remove('userRole');
    navigate("/");
  };

  const getDisplayTitle = () => pageTitle || 'School ERP Portal';
  
  const getBreadcrumb = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Home / Dashboard';
    if (!pageTitle) return 'Home / School ERP Portal';
    const masterPaths = ['/college', '/university', '/academic-year', '/category', '/degree', '/discipline', '/program', '/courses', '/educoursetype', '/component-master', '/credit-hours', '/programme-year', '/semster'];
    if (masterPaths.some(p => path.includes(p))) return `Master Data / ${pageTitle}`;
    return `Home / ${pageTitle}`;
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true));
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  };
  
  const sidebarWidth = isMobile ? '0' : (document.getElementById('layout-container')?.classList.contains('sidebar-collapsed') ? '70px' : '280px');

  return (
    <>
      <header className="header-container" style={{ left: sidebarWidth, width: `calc(100% - ${sidebarWidth})`, padding: isMobile ? "0 16px" : "0 32px" }}>
        <div className="header-left">
          {isMobile && (
            <button className="mobile-toggle-btn action-button" onClick={() => window.dispatchEvent(new CustomEvent('toggleMobileSidebar'))}>
              <FaBars size={18} />
            </button>
          )}
          <div className="title-section">
            <h1 className="page-title-header" style={{ fontSize: isMobile ? "18px" : "24px" }}>
              {getDisplayTitle()}
            </h1>
            {!isMobile && <div className="breadcrumb-header">{getBreadcrumb()}</div>}
          </div>
          {!isMobile && (
            <div className="search-container">
              <FaSearch className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search students, staff, courses..."
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
              />
            </div>
          )}
        </div>
        <div className="header-right">
          {isMobile && <ActionButton onClick={() => setShowSearch(!showSearch)} icon={<FaSearch size={18} />} title="Search" />}
          <ActionButton
            onClick={toggleFullscreen}
            icon={isFullscreen ? <FaCompress size={16} /> : <FaExpandArrowsAlt size={16} />}
            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            className={isMobile ? "d-none" : ""}
          />
          <NotificationDropdown notifications={notifications} />
          <UserProfileDropdown 
            username={username}
            userRole={userRole}
            isMobile={isMobile}
            navigate={navigate}
            handleLogout={handleLogout}
          />
        </div>
      </header>
      <MobileSearchOverlay 
        isMobile={isMobile}
        showSearch={showSearch}
        setShowSearch={setShowSearch}
        globalSearch={globalSearch}
        setGlobalSearch={setGlobalSearch}
      />
    </>
  );
}

// Helper Components using CSS classes
const ActionButton = ({ onClick, icon, title, className = "" }) => (
  <button onClick={onClick} className={`action-button ${className}`} title={title}>
    {icon}
  </button>
);

const NotificationDropdown = ({ notifications }) => (
  <Dropdown align="end">
    <Dropdown.Toggle as={ActionButton} icon={<FaBell size={18} />} />
    <Dropdown.Menu>
      <div style={{ fontWeight: '700', fontSize: '18px', marginBottom: '16px', padding: '0 1.5rem' }}>Notifications</div>
      {notifications.map((n) => (
        <Dropdown.Item key={n.id} style={{ whiteSpace: 'normal' }}>
          <div style={{ fontWeight: '600' }}>{n.title}</div>
          <small>{n.time}</small>
        </Dropdown.Item>
      ))}
    </Dropdown.Menu>
  </Dropdown>
);

const UserProfileDropdown = ({ username, userRole, isMobile, navigate, handleLogout }) => (
  <Dropdown align="end">
    <Dropdown.Toggle as="div" className="user-profile-toggle custom-dropdown-toggle">
      <div className="user-avatar">{username.charAt(0).toUpperCase()}</div>
      {!isMobile && (
        <div>
          <div style={{ fontWeight: '600' }}>{username}</div>
          <div style={{ fontSize: '12px' }}>{userRole}</div>
        </div>
      )}
      {/* The Dropdown.Toggle component provides its own arrow, so the explicit FaChevronDown icon was redundant. */}
    </Dropdown.Toggle>
    <Dropdown.Menu>
      <Dropdown.Item onClick={() => navigate("/profile")}><FaUser className="me-2"/> My Profile</Dropdown.Item>
      <Dropdown.Item onClick={() => navigate("/settings")}><FaCog className="me-2"/> Settings</Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Item onClick={handleLogout} className="text-danger"><FaSignOutAlt className="me-2"/> Sign Out</Dropdown.Item>
    </Dropdown.Menu>
  </Dropdown>
);

const MobileSearchOverlay = ({ isMobile, showSearch, setShowSearch, globalSearch, setGlobalSearch }) => {
    if (!isMobile || !showSearch) return null;
    return (
      <div className="mobile-search-overlay">
        <input type="text" placeholder="Search..." autoFocus value={globalSearch} onChange={(e) => setGlobalSearch(e.target.value)} onKeyPress={(e) => { if (e.key === 'Enter') setShowSearch(false); }} className="search-input" />
      </div>
    );
};
